import React, {HTMLProps, useContext} from 'react';
import reactStringReplace from 'react-string-replace';
import app from '@src/helpers/app';
import classnames from '@src/helpers/classnames';
import {ITranslateProps} from '@src/@interfaces/components/Translate/ITranslateProps';
import {LocaleContext} from '@src/contexts/LocaleContext';

type ResolveProps<P, E> = P extends Record<any, any> ?
        (Omit<HTMLProps<E>, keyof P> & P) :
        HTMLProps<E>;

export function ReplaceWithComponents({replace, message}: {
    message: string,
    replace?: ITranslateProps['replace'],
}) {
    let index = 0;

    const doReplace = (message: string | React.ReactNode[], keyPrefix = '') => {
        const result = reactStringReplace(message, /:([^\s:]+)/guim, (name, i, offset) => {
            const replacement = replace?.[name];

            return <React.Fragment key={`${++index}-${keyPrefix}-${name}-${i}-${offset}`}>
                {(() => {
                    if (!replacement) {
                        return `:${name}`;
                    }

                    if (React.isValidElement(replacement)) {
                        return replacement;
                    }

                    if (typeof replacement === 'function') {
                        return replacement(name);
                    }

                    return replacement;
                })()}
            </React.Fragment>;
        });

        // \* -> *
        return reactStringReplace(
                result,
                '\\*',
                (match, i, offset) => {
                    return <React.Fragment key={`${++index}-${keyPrefix}-${i}-${offset}`}>*</React.Fragment>;
                }
        );
    };

    let result: ReturnType<typeof doReplace> | string = message;

    // `code` -> <code>code</code>
    result = reactStringReplace(
            result,
            /(?<!\\)`([^`]+)(?<!\\)`/guim,
            (match, i) => {
                return <code key={match + i}>{doReplace(match, match + i)}</code>;
            }
    );

    // **bold** -> <b>bold</b>
    result = reactStringReplace(
            result,
            /(?<!\\)\*{2}([^*]+)(?<!\\)\*{2}/guim,
            (match, i) => <b key={match + i}>{doReplace(match, match + i)}</b>
    );

    // *cursive* -> <i>cursive</i>
    result = reactStringReplace(
            result,
            /(?<!\\)\*([^*]+)(?<!\\)\*/guim,
            (match, i) => <i key={match + i}>{doReplace(match, match + i)}</i>
    );

    // [list](url) -> <a href="url">link</a>
    result = reactStringReplace(
            result,
            /((?<!\\)\[[^()\[\]]*?(?<!\\)\](?<!\\)\(.*?(?<!\\)\))/guim,
            (match, i) => {
                const matches = [...match.matchAll(/\[(.*?)\]\((.*?)\)/gi)];
                if (matches.length !== 1 || matches[0].length !== 3) {
                    return result;
                }

                return <a
                        key={match + i}
                        href={matches[0][2]}
                        rel="noreferrer"
                        target="_blank"
                >
                    {doReplace(matches[0][1], match + i)}
                </a>;
            }
    );

    return <>{doReplace(result)}</>;
}

export default function Translate<
        HTML extends HTMLElement = HTMLElement,
        ExtraProps = unknown
>(props: ITranslateProps & ResolveProps<ExtraProps, HTML>) {
    const context = useContext(LocaleContext);
    const translator = app('trans');

    const {
        tag = 'span',
        choice,
        message: key,
        locale = context.locale,
        replace,
        defaultMessage,
        withComponentsAsReplacements,
        ...rest
    } = props;

    const fallbackLocale = translator.getFallbackLocale();

    const translate = (locale: string) => {
        const replacements = (withComponentsAsReplacements ? null : replace) ?? {};
        if (choice === undefined) {
            return translator.get(key, replacements as any, null, locale);
        }

        return translator.choice(key, choice, replacements as any, null, locale);
    };

    const message = translate(locale);
    const fallbackMessage = (message || locale === fallbackLocale) ? null : translate(fallbackLocale);

    const Tag: any = tag;
    const finalMessage = message ?? fallbackMessage ?? defaultMessage ?? key;

    return <Tag {...rest} className={classnames(rest.className, 'translated-message', {
        'translated-message--not-found': !message,
        'translated-message--is-fallback': fallbackMessage,
    })}>
        {props.withComponentsAsReplacements ? (
                <ReplaceWithComponents
                        message={finalMessage}
                        replace={replace}
                />
        ) : finalMessage}
    </Tag>;
}
